import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Scale, TrendingUp, Landmark, ReceiptText, Trash2 } from "lucide-react";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Finance() {
  return (
    <div className="space-y-4" data-testid="finance-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Accounts & Finance</h1>
        <p className="text-sm text-muted-foreground">Chart of accounts, double-entry journal, trial balance, P&L, balance sheet, GSTR reports.</p>
      </div>

      <Tabs defaultValue="coa">
        <TabsList>
          <TabsTrigger value="coa" data-testid="tab-coa">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">Journal</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb">Trial Balance</TabsTrigger>
          <TabsTrigger value="pl" data-testid="tab-pl">P&L</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst">GST Report</TabsTrigger>
        </TabsList>

        <TabsContent value="coa" className="mt-4"><ChartOfAccounts /></TabsContent>
        <TabsContent value="journal" className="mt-4"><Journal /></TabsContent>
        <TabsContent value="tb" className="mt-4"><TrialBalance /></TabsContent>
        <TabsContent value="pl" className="mt-4"><ProfitLoss /></TabsContent>
        <TabsContent value="bs" className="mt-4"><BalanceSheet /></TabsContent>
        <TabsContent value="gst" className="mt-4"><GSTReport /></TabsContent>
      </Tabs>
    </div>
  );
}

function ChartOfAccounts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["accounts"], queryFn: () => api.get("/finance/accounts").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "asset", subtype: "", opening_balance: 0 });
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const create = useMutation({
    mutationFn: () => api.post("/finance/accounts", form),
    onSuccess: () => { toast.success("Account created"); qc.invalidateQueries({ queryKey: ["accounts"] }); setOpen(false); setForm({ code: "", name: "", type: "asset", subtype: "", opening_balance: 0 }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const seed = useMutation({
    mutationFn: () => api.post("/finance/seed-chart"),
    onSuccess: (r) => { toast.success(`Seeded ${r.data.inserted} accounts (total ${r.data.total})`); qc.invalidateQueries({ queryKey: ["accounts"] }); },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Landmark size={18} /> Chart of Accounts</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => seed.mutate()} data-testid="btn-seed-coa">Seed defaults</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="btn-new-account"><Plus size={14} className="mr-1" />New account</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New account</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code</Label><Input value={form.code} onChange={upd("code")} placeholder="1099" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={upd("type")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asset">Asset</SelectItem>
                      <SelectItem value="liability">Liability</SelectItem>
                      <SelectItem value="equity">Equity</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={upd("name")} placeholder="Cash at Bank of India" /></div>
                <div><Label>Subtype</Label><Input value={form.subtype} onChange={upd("subtype")} placeholder="bank / cash / debtors" /></div>
                <div><Label>Opening balance</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={upd("opening_balance")} /></div>
              </div>
              <DialogFooter><Button onClick={() => create.mutate()} disabled={!form.code || !form.name}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Name</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Subtype</th><th className="px-4 py-2 text-right">Opening</th></tr>
          </thead>
          <tbody>
            {(data?.items || []).map((a) => (
              <tr key={a.id} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2 font-mono">{a.code}</td>
                <td className="px-4 py-2 font-semibold">{a.name}</td>
                <td className="px-4 py-2"><span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold uppercase">{a.type}</span></td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{a.subtype || "—"}</td>
                <td className="px-4 py-2 text-right font-mono">{money(a.opening_balance)}</td>
              </tr>
            ))}
            {(!data?.items || data.items.length === 0) && (<tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No accounts yet — click <b>Seed defaults</b> to bootstrap a standard chart.</td></tr>)}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function Journal() {
  const qc = useQueryClient();
  const { data: accountsData } = useQuery({ queryKey: ["accounts"], queryFn: () => api.get("/finance/accounts").then((r) => r.data) });
  const { data } = useQuery({ queryKey: ["journals"], queryFn: () => api.get("/finance/journals").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [narration, setNarration] = useState("");
  const [reference, setReference] = useState("");
  const [lines, setLines] = useState([
    { _lid: "L1", account_id: "", debit: 0, credit: 0, narration: "" },
    { _lid: "L2", account_id: "", debit: 0, credit: 0, narration: "" },
  ]);
  const setLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: k === "account_id" || k === "narration" ? v : Number(v) } : l));
  const addLine = () => setLines((prev) => [...prev, { _lid: `L${Date.now()}${prev.length}`, account_id: "", debit: 0, credit: 0, narration: "" }]);
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const totalDr = lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCr = lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

  const post = useMutation({
    mutationFn: () => api.post("/finance/journals", { narration, reference, lines: lines.filter((l) => l.account_id) }),
    onSuccess: (r) => { toast.success(`Posted ${r.data.code}`); qc.invalidateQueries({ queryKey: ["journals"] }); setOpen(false); setLines([{ _lid: "L1", account_id: "", debit: 0, credit: 0, narration: "" }, { _lid: "L2", account_id: "", debit: 0, credit: 0, narration: "" }]); setNarration(""); setReference(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><ReceiptText size={18} /> Journal Entries</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-journal"><Plus size={14} className="mr-1" />New entry</Button></DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>New journal entry (double-entry)</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Bill #, Cheque #, etc." /></div>
                <div><Label>Narration</Label><Input value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="Purpose of the entry" /></div>
              </div>
              <div className="rounded-lg border border-border/60">
                <div className="grid grid-cols-12 border-b border-border/60 bg-muted/40 px-2 py-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  <div className="col-span-5">Account</div>
                  <div className="col-span-3 text-right">Debit</div>
                  <div className="col-span-3 text-right">Credit</div>
                  <div className="col-span-1"></div>
                </div>
                {lines.map((l, i) => (
                  <div key={l._lid} className="grid grid-cols-12 gap-2 border-b border-border/40 p-2">
                    <div className="col-span-5">
                      <Select value={l.account_id} onValueChange={(v) => setLine(i, "account_id", v)}>
                        <SelectTrigger data-testid={`j-line-account-${i}`}><SelectValue placeholder="Select account" /></SelectTrigger>
                        <SelectContent>
                          {(accountsData?.items || []).map((a) => (<SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3"><Input type="number" step="0.01" value={l.debit || ""} onChange={(e) => setLine(i, "debit", e.target.value)} className="text-right font-mono" /></div>
                    <div className="col-span-3"><Input type="number" step="0.01" value={l.credit || ""} onChange={(e) => setLine(i, "credit", e.target.value)} className="text-right font-mono" /></div>
                    <div className="col-span-1 flex items-center"><Button size="icon" variant="ghost" onClick={() => rmLine(i)} disabled={lines.length <= 2}><Trash2 size={14} /></Button></div>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-border/60 bg-muted/20 p-2 text-sm">
                  <Button size="sm" variant="outline" onClick={addLine}><Plus size={12} className="mr-1" /> Add line</Button>
                  <div className="flex gap-4 font-mono text-xs">
                    <span>DR: <b>{money(totalDr)}</b></span>
                    <span>CR: <b>{money(totalCr)}</b></span>
                    <span className={balanced ? "text-status-delivered" : "text-status-cancelled"}>{balanced ? "✓ Balanced" : `Diff ${money(Math.abs(totalDr - totalCr))}`}</span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={() => post.mutate()} disabled={!balanced || post.isPending} data-testid="btn-post-journal">{post.isPending ? "Posting…" : "Post entry"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Narration</th><th className="px-4 py-2 text-right">Debit</th><th className="px-4 py-2 text-right">Credit</th></tr>
          </thead>
          <tbody>
            {(data?.items || []).map((j) => (
              <tr key={j.id} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2 font-mono">{j.code}</td>
                <td className="px-4 py-2 text-xs">{new Date(j.date).toLocaleDateString()}</td>
                <td className="px-4 py-2">{j.narration || <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-2 text-right font-mono">{money(j.total_debit)}</td>
                <td className="px-4 py-2 text-right font-mono">{money(j.total_credit)}</td>
              </tr>
            ))}
            {(data?.items || []).length === 0 && (<tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No journal entries yet.</td></tr>)}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function TrialBalance() {
  const { data } = useQuery({ queryKey: ["trial-balance"], queryFn: () => api.get("/finance/trial-balance").then((r) => r.data) });
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Scale size={18} /> Trial Balance <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${data?.matched ? "bg-status-delivered/10 text-status-delivered" : "bg-status-cancelled/10 text-status-cancelled"}`}>{data?.matched ? "✓ Balanced" : "✗ Out of balance"}</span></CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Account</th><th className="px-4 py-2">Type</th><th className="px-4 py-2 text-right">Debit</th><th className="px-4 py-2 text-right">Credit</th></tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.code} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2 font-mono">{r.code}</td>
                <td className="px-4 py-2">{r.name}</td>
                <td className="px-4 py-2 text-xs uppercase text-muted-foreground">{r.type}</td>
                <td className="px-4 py-2 text-right font-mono">{r.debit ? money(r.debit) : "—"}</td>
                <td className="px-4 py-2 text-right font-mono">{r.credit ? money(r.credit) : "—"}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-primary/40 bg-primary/5 text-sm font-bold"><td colSpan={3} className="px-4 py-3 text-right">TOTAL</td><td className="px-4 py-3 text-right font-mono">{money(data?.total_debit)}</td><td className="px-4 py-3 text-right font-mono">{money(data?.total_credit)}</td></tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ProfitLoss() {
  const { data } = useQuery({ queryKey: ["pl"], queryFn: () => api.get("/finance/profit-loss").then((r) => r.data) });
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>Income</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <tbody>
              {(data?.income || []).map((r) => (<tr key={r.code} className="border-b border-border/40"><td className="px-4 py-2 font-mono text-xs">{r.code}</td><td className="px-4 py-2">{r.name}</td><td className="px-4 py-2 text-right font-mono">{money(r.amount)}</td></tr>))}
              <tr className="bg-status-delivered/5 font-bold"><td colSpan={2} className="px-4 py-2">Total Income</td><td className="px-4 py-2 text-right font-mono text-status-delivered">{money(data?.total_income)}</td></tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Expenses</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <tbody>
              {(data?.expenses || []).map((r) => (<tr key={r.code} className="border-b border-border/40"><td className="px-4 py-2 font-mono text-xs">{r.code}</td><td className="px-4 py-2">{r.name}</td><td className="px-4 py-2 text-right font-mono">{money(r.amount)}</td></tr>))}
              <tr className="bg-status-cancelled/5 font-bold"><td colSpan={2} className="px-4 py-2">Total Expenses</td><td className="px-4 py-2 text-right font-mono text-status-cancelled">{money(data?.total_expense)}</td></tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
      <Card className="lg:col-span-2 border-primary/40 bg-primary/5">
        <CardContent className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3"><TrendingUp size={28} className="text-primary" /><div><div className="text-xs uppercase tracking-widest text-muted-foreground">Net Profit / (Loss)</div><div className="font-display text-3xl font-black">{money(data?.net_profit)}</div></div></div>
          <div className="text-xs text-muted-foreground">Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}</div>
        </CardContent>
      </Card>
    </div>
  );
}

function BalanceSheet() {
  const { data } = useQuery({ queryKey: ["bs"], queryFn: () => api.get("/finance/balance-sheet").then((r) => r.data) });
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card><CardHeader><CardTitle>Assets</CardTitle></CardHeader><CardContent className="p-0">
        <table className="w-full text-sm"><tbody>
          {(data?.assets || []).map((r) => (<tr key={r.code} className="border-b border-border/40"><td className="px-4 py-2 font-mono text-xs">{r.code}</td><td className="px-4 py-2">{r.name}</td><td className="px-4 py-2 text-right font-mono">{money(r.amount)}</td></tr>))}
          <tr className="bg-primary/5 font-bold"><td colSpan={2} className="px-4 py-2">Total Assets</td><td className="px-4 py-2 text-right font-mono">{money(data?.total_assets)}</td></tr>
        </tbody></table>
      </CardContent></Card>
      <Card><CardHeader><CardTitle>Liabilities & Equity</CardTitle></CardHeader><CardContent className="p-0">
        <table className="w-full text-sm"><tbody>
          {(data?.liabilities || []).map((r) => (<tr key={r.code} className="border-b border-border/40"><td className="px-4 py-2 font-mono text-xs">{r.code}</td><td className="px-4 py-2">{r.name}</td><td className="px-4 py-2 text-right font-mono">{money(r.amount)}</td></tr>))}
          <tr className="bg-muted/40 font-semibold"><td colSpan={2} className="px-4 py-2">Sub-total Liabilities</td><td className="px-4 py-2 text-right font-mono">{money(data?.total_liabilities)}</td></tr>
          {(data?.equity || []).map((r) => (<tr key={r.code + r.name} className="border-b border-border/40"><td className="px-4 py-2 font-mono text-xs">{r.code}</td><td className="px-4 py-2">{r.name}</td><td className="px-4 py-2 text-right font-mono">{money(r.amount)}</td></tr>))}
          <tr className="bg-primary/5 font-bold"><td colSpan={2} className="px-4 py-2">Total L + E</td><td className="px-4 py-2 text-right font-mono">{money(data?.total_liab_equity)}</td></tr>
        </tbody></table>
      </CardContent></Card>
      <div className={`lg:col-span-2 text-center text-sm ${data?.matched ? "text-status-delivered" : "text-status-cancelled"}`}>{data?.matched ? "✓ Balanced: Assets = Liabilities + Equity" : "✗ Difference — check journal entries"}</div>
    </div>
  );
}

function GSTReport() {
  const [month, setMonth] = useState("");
  const { data, refetch } = useQuery({ queryKey: ["gst-summary", month], queryFn: () => api.get(`/finance/gst/summary${month ? `?month=${month}` : ""}`).then((r) => r.data) });
  return (
    <div className="space-y-4">
      <Card><CardContent className="flex items-center gap-3 p-4">
        <Label className="mb-0">Month (YYYY-MM):</Label>
        <Input value={month} onChange={(e) => setMonth(e.target.value)} placeholder="2026-02" className="max-w-40 font-mono" />
        <Button onClick={() => refetch()} data-testid="btn-gst-run">Run report</Button>
      </CardContent></Card>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground uppercase">Invoices</div><div className="font-display text-2xl font-black">{data?.invoice_count || 0}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground uppercase">Taxable value</div><div className="font-display text-xl font-black">{money(data?.outward_taxable_value)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground uppercase">Total GST</div><div className="font-display text-xl font-black text-primary">{money(data?.total_tax)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground uppercase">Invoice total</div><div className="font-display text-xl font-black">{money(data?.invoice_total)}</div></CardContent></Card>
      </div>
      <Card><CardHeader><CardTitle>HSN-wise summary</CardTitle></CardHeader><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground"><tr><th className="px-4 py-2">HSN</th><th className="px-4 py-2 text-right">Qty (L)</th><th className="px-4 py-2 text-right">Taxable</th><th className="px-4 py-2 text-right">CGST</th><th className="px-4 py-2 text-right">SGST</th><th className="px-4 py-2 text-right">IGST</th></tr></thead>
          <tbody>
            {(data?.hsn_summary || []).map((r) => (<tr key={r.hsn} className="border-b border-border/40"><td className="px-4 py-2 font-mono">{r.hsn}</td><td className="px-4 py-2 text-right font-mono">{r.quantity?.toFixed(2)}</td><td className="px-4 py-2 text-right font-mono">{money(r.taxable)}</td><td className="px-4 py-2 text-right font-mono">{money(r.cgst)}</td><td className="px-4 py-2 text-right font-mono">{money(r.sgst)}</td><td className="px-4 py-2 text-right font-mono">{money(r.igst)}</td></tr>))}
            {(!data?.hsn_summary || data.hsn_summary.length === 0) && (<tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No data for selected period</td></tr>)}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
